import { WindowScroller, Collection } from "react-virtualized";
import { InfiniteScroll } from "antd-mobile";
import { useTranslations } from "next-intl";
import cx from "classnames";
import {
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";

import Styles from "./mIndex.module.scss";

const getContainerViewportWidth = (): number => {
  if (typeof window === "undefined") return 375;
  const nextElement = document.getElementById("__next");
  return (
    nextElement?.clientWidth ||
    document.body?.clientWidth ||
    window.innerWidth ||
    375
  );
};

const scaleDesignPxByWidth = (designPx: number, viewportWidth: number): number => {
  const width = viewportWidth || 375;
  return (designPx / 2) * (width / 375);
};

// 瀑布流布局常量
const WATERFALL_CONSTANTS = {
  get COLUMN_WIDTH() {
    return scaleDesignPxByWidth(328, getContainerViewportWidth());
  },
  get GUTTER_WIDTH() {
    return scaleDesignPxByWidth(30, getContainerViewportWidth());
  },
  get GUTTER_HEIGHT() {
    return scaleDesignPxByWidth(30, getContainerViewportWidth());
  },
  get PADDING_HORIZONTAL() {
    return scaleDesignPxByWidth(32, getContainerViewportWidth());
  },
  get TOTAL_PADDING() {
    return this.PADDING_HORIZONTAL * 2;
  }, // 左右各32px
};

// 瀑布流组件属性接口
export interface VirtualWaterfallProps<T> {
  /** 列数 */
  columns?: number;
  /** 初始化数据 */
  initData?: Array<T>;
  /** 列宽 */
  columnWidth?: number;
  /** 列间距 */
  gutterWidth?: number;
  /** 行间距 */
  gutterHeight?: number;
  /** 初始分页位置 */
  initialOffset?: number;
  /** 初始是否存在下一页 */
  initialHasNextPage?: boolean;
  /** 组件销毁事件 */
  onDestory?: (offset?: number) => void;
  /** 加载状态变化回调 */
  onLoadingChange?: (isLoading: boolean) => void;
  /** 数据变化回调 */
  onDataChange?: (data: T[]) => void;
  /** 加载中组件 */
  renderBottomItem?: (isLoading: boolean, hasNextPage: boolean) => ReactNode;
  /** 空状态渲染 */
  renderEmpty?: () => ReactNode;
  /** 元素组件 */
  renderItem: (item: T, index: number) => ReactNode;
  /** 获取元素高度的函数 */
  getItemHeight?: (item: T, index: number) => number;
  /** 滚动事件回调 */
  onScroll?: ({}: { scrollTop: number; scrollLeft: number }) => void;
  /** 自动预加载到指定条数（用于滚动恢复） */
  autoLoadUntil?: number;
  /** 需要从列表中移除的 item key（配合 itemKeyFn 使用，变化时触发过滤） */
  excludeItemKey?: { value: string | number; ts: number } | null;
  /** 获取 item key 的函数（用于 excludeItemKey 匹配） */
  itemKeyFn?: (item: T) => string | number;
  /** 加载数据函数 */
  queryFn: (
    offset?: number,
    size?: number
  ) => Promise<{
    data: Array<T>;
    nextOffset: number;
    hasNextPage: boolean;
  }>;
}

// 单元格位置和尺寸接口
interface CellSizeAndPosition {
  height: number;
  width: number;
  x: number;
  y: number;
}

/**
 * 虚拟瀑布流组件
 * @param props 组件属性
 * @returns 瀑布流组件
 */
export function VirtualWaterfall<T>(props: VirtualWaterfallProps<T>) {
  const {
    initData = [],
    initialOffset = 0,
    columns = 2,
    initialHasNextPage = true,
    renderBottomItem,
    renderEmpty,
    renderItem,
    onDestory,
    onLoadingChange,
    onDataChange,
    queryFn,
    onScroll = () => {},
    autoLoadUntil,
    excludeItemKey,
    itemKeyFn,
  } = props;

  const t = useTranslations();
  const collectionRef = useRef<Collection | null>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const isLocked = useRef<boolean>(false);
  const lastRequestedOffsetRef = useRef<number | null>(null);

  // 状态管理
  const [offset, setOffset] = useState<number>(initialOffset);
  const [data, setData] = useState<T[]>([...initData]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(initialHasNextPage);
  const [loadError, setLoadError] = useState(false);
  // 组件在滚动容器中的偏移，用于 WindowScroller -> Collection 的 scrollTop 修正
  const [containerOffsetTop, setContainerOffsetTop] = useState(0);

  const [containerWidth, setContainerWidth] = useState(
    getContainerViewportWidth()
  );

  // 动态计算列宽
  const dynamicColumnWidth = useMemo(() => {
    // 减去左右内边距和列间距，然后平均分配给每列
    const availableWidth = containerWidth - WATERFALL_CONSTANTS.TOTAL_PADDING;
    const totalGutterWidth = WATERFALL_CONSTANTS.GUTTER_WIDTH * (columns - 1);
    const calculatedWidth = (availableWidth - totalGutterWidth) / columns;

    // 如果计算出的宽度小于最小宽度，则使用传入的默认宽度
    return Math.max(calculatedWidth, WATERFALL_CONSTANTS.COLUMN_WIDTH);
  }, [containerWidth, columns]);

  // 根据数据计算每个项目的高度
  const getItemHeight = useCallback(
    (index: number) => {
      const item = data[index];
      // 根据是否有图片来确定高度
      return (item as any)?.covers?.length >= 1
        ? scaleDesignPxByWidth(438, containerWidth)
        : scaleDesignPxByWidth(156, containerWidth);
    },
    [data, containerWidth]
  );

  // 使用 useMemo 来缓存瀑布流布局计算结果

  const layoutInfo = useMemo(() => {
    const columnHeights = new Array(columns).fill(0);
    const positions = new Map();
    let maxHeight = 0;

    data.forEach((_, index) => {
      const itemHeight = getItemHeight(index);
      const minColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
      const top = columnHeights[minColumnIndex];

      positions.set(index, {
        columnIndex: minColumnIndex,
        top,
        left:
          minColumnIndex === 0
            ? WATERFALL_CONSTANTS.PADDING_HORIZONTAL
            : undefined,
        right:
          minColumnIndex === 1
            ? WATERFALL_CONSTANTS.PADDING_HORIZONTAL
            : undefined,
        width: dynamicColumnWidth,
        height: itemHeight,
      });

      columnHeights[minColumnIndex] =
        top + itemHeight + WATERFALL_CONSTANTS.GUTTER_HEIGHT;
      maxHeight = Math.max(maxHeight, columnHeights[minColumnIndex]);
    });

    return {
      positions,
      totalHeight: maxHeight,
      columnHeights,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, columns, getItemHeight, dynamicColumnWidth, containerWidth]);

  // 单元格尺寸和位置计算器
  const cellSizeAndPositionGetter = useCallback(
    ({ index }: { index: number }): CellSizeAndPosition => {
      // 计算单元格尺寸
      const width = dynamicColumnWidth;
      const height = getItemHeight ? getItemHeight(index) : 200;

      // 计算位置
      const x = layoutInfo.positions.get(index)?.left
        ? layoutInfo.positions.get(index)?.left
        : dynamicColumnWidth +
          WATERFALL_CONSTANTS.GUTTER_WIDTH +
          layoutInfo.positions.get(index)?.right;
      const y =
        (layoutInfo.positions.get(index)?.top ?? 0) +
        scaleDesignPxByWidth(20, containerWidth);

      return {
        height,
        width,
        x,
        y,
      };
    },
    [dynamicColumnWidth, getItemHeight, layoutInfo.positions, containerWidth]
  );

  // 单元格渲染器
  const cellRenderer = useCallback(
    ({
      index,
      key,
      style,
    }: {
      index: number;
      key: number;
      style: React.CSSProperties;
    }) => {
      if (index >= data.length) return null;

      return (
        <div
          key={key}
          className={Styles.cellItem}
          // eslint-disable-next-line
          style={style} // react-virtualized 需要内联样式来定位元素
        >
          {renderItem(data[index], index)}
        </div>
      );
    },
    [data, renderItem]
  );

  // 无内容渲染器
  const noContentRenderer = useCallback(() => {
    if (isLoading) return null;
    if (renderEmpty) return renderEmpty();
    return <div className={Styles.noContent}>{t("no_data")}</div>;
  }, [t, renderEmpty, isLoading]);

  // 渲染底部内容
  const renderBottom = useCallback(() => {
    if (renderBottomItem) {
      return renderBottomItem(isLoading, hasNextPage);
    }

    if (!isLoading && !hasNextPage) {
      // 如果没有数据，显示空状态（noContentRenderer），不显示“没有更多数据”
      if (data.length === 0) return null;
      return <div className={Styles.noMore}>{t("no_more_data")}</div>;
    }

    if (isLoading) {
      return (
        <div className={Styles.loading}>
          <div className={Styles.loadingSpinner}></div>
          <span>加载中...</span>
        </div>
      );
    }

    if (loadError) {
      return <div className={Styles.noMore}>加载失败，请稍后重试</div>;
    }

    return null;
  }, [renderBottomItem, isLoading, hasNextPage, loadError, t, data.length]);

  // 获取下一页数据
  const fetchNextPage = useCallback(
    async (initOffset?: number) => {
      if (isLoading) return;

      const currentOffset = (offset || initOffset || 0) as number;
      if (lastRequestedOffsetRef.current === currentOffset) return;
      lastRequestedOffsetRef.current = currentOffset;
      setIsLoading(true);

      try {
        const res = await queryFn(currentOffset);
        const { data: rawData, nextOffset, hasNextPage = false } = res;
        setData((prevData) => [...prevData, ...rawData]);
        setOffset(nextOffset);
        setHasNextPage(hasNextPage);
        setLoadError(false);
      } catch (error) {
        console.error("获取数据失败:", error);
        setLoadError(true);
        setHasNextPage(false);
        lastRequestedOffsetRef.current = null;
      } finally {
        setIsLoading(false);
      }
    },
    [offset, queryFn, isLoading]
  );

  // 自动预加载到目标条数（用于返回滚动位置前补齐数据）
  useEffect(() => {
    if (!autoLoadUntil || autoLoadUntil <= data.length) return;
    if (!hasNextPage || isLoading) return;
    fetchNextPage();
  }, [autoLoadUntil, data.length, hasNextPage, isLoading, fetchNextPage]);

  // 挂载时若 initData 为空且有下一页，主动触发首次加载
  useEffect(() => {
    if (data.length === 0 && hasNextPage && !isLoading) {
      fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 监听 excludeItemKey 变化，从列表中移除对应 item
  useEffect(() => {
    if (!excludeItemKey || !itemKeyFn) return;
    const keyToRemove = excludeItemKey.value;
    setData((prev) => {
      const filtered = prev.filter((item) => itemKeyFn(item) !== keyToRemove);
      if (filtered.length !== prev.length) {
        setOffset((o) => Math.max(0, o - (prev.length - filtered.length)));
      }
      return filtered;
    });
  }, [excludeItemKey, itemKeyFn]);

  // 监听加载状态变化
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);

  // 监听数据变化
  useEffect(() => {
    if (onDataChange) {
      onDataChange(data);
    }
  }, [data, onDataChange]);

  // 添加屏幕方向变化和尺寸变化的监听处理
  useEffect(() => {
    // 处理屏幕旋转和尺寸变化的回调函数
    const handleResize = () => {
      // 使用 setTimeout 确保在方向变化完成后再重新计算
      setTimeout(() => {
        // 重新获取容器宽度
        setContainerWidth(getContainerViewportWidth());
        setContainerOffsetTop(parentRef.current?.offsetTop || 0);

        // 如果列表中有数据，则重新计算所有项目的位置
        if (data.length > 0) {
          // 触发 Collection 组件重新计算所有单元格的尺寸和位置
          collectionRef?.current?.recomputeCellSizesAndPositions();
        }
      }, 100);
    };

    // 处理屏幕方向变化的回调函数
    const handleOrientationChange = () => {
      // 屏幕方向变化时，需要等待一段时间让浏览器完成布局调整
      setTimeout(() => {
        // 重新获取容器宽度
        setContainerWidth(getContainerViewportWidth());
        setContainerOffsetTop(parentRef.current?.offsetTop || 0);

        // 重置滚动位置到顶部
        const nextElement = document.querySelector("#__next") as HTMLElement;
        if (nextElement) {
          nextElement.scrollTop = 0;
        } else {
          window.scrollTo(0, 0);
        }

        // 触发 Collection 组件重新计算所有单元格的尺寸和位置
        collectionRef?.current?.recomputeCellSizesAndPositions();
      }, 300);
    };

    // 初始化时重新计算
    collectionRef?.current?.recomputeCellSizesAndPositions();
    setContainerOffsetTop(parentRef.current?.offsetTop || 0);

    // 添加各种事件监听
    window.addEventListener("orientationchange", handleOrientationChange);
    window.addEventListener("resize", handleResize);

    // 组件卸载时移除事件监听
    return () => {
      window.removeEventListener("orientationchange", handleOrientationChange);
      window.removeEventListener("resize", handleResize);
    };
  }, [data]);

  // 组件卸载时的清理
  useEffect(() => {
    return () => {
      if (onDestory) {
        onDestory(offset);
      }
    };
  }, [onDestory, offset]);

  // 渲染瀑布流
  const listSegment = useMemo(() => {
    // 统一使用页面滚动容器（__next），让在列表上滑动也能带动整页滚动 + tabs 吸顶
    const scrollElement =
      (typeof document !== "undefined" &&
        (document.getElementById("__next") as HTMLElement | null)) ||
      window;

    return (
      <div ref={parentRef} className={cx(Styles.root)}>
        <WindowScroller
          scrollElement={scrollElement}
          onScroll={onScroll}
        >
          {({ height, scrollTop, onChildScroll }) => {
            return (
              <Collection
                ref={collectionRef}
                autoHeight
                isScrolling={false}
                cellCount={data.length}
                cellRenderer={cellRenderer}
                cellSizeAndPositionGetter={cellSizeAndPositionGetter}
                className={Styles.collection}
                height={height ?? 0}
                noContentRenderer={noContentRenderer}
                verticalOverscanSize={scaleDesignPxByWidth(800, containerWidth)}
                width={containerWidth}
                scrollTop={Math.max(0, (scrollTop ?? 0) - containerOffsetTop)}
                onScroll={onChildScroll}
                style={{
                  paddingBottom: scaleDesignPxByWidth(20, containerWidth),
                  paddingLeft: WATERFALL_CONSTANTS.PADDING_HORIZONTAL,
                  paddingRight: WATERFALL_CONSTANTS.PADDING_HORIZONTAL,
                }}
              />
            );
          }}
        </WindowScroller>

        {/* 无限滚动加载 */}
        <InfiniteScroll
          loadMore={async () => {
            if (!isLocked.current && !loadError) {
              isLocked.current = true;
              await fetchNextPage();
              isLocked.current = false;
            }
          }}
          hasMore={hasNextPage}
          threshold={100}
        >
          {renderBottom()}
        </InfiniteScroll>
      </div>
    );
  }, [
    onScroll,
    hasNextPage,
    renderBottom,
    data.length,
    cellRenderer,
    cellSizeAndPositionGetter,
    noContentRenderer,
    containerWidth,
    containerOffsetTop,
    fetchNextPage,
  ]);

  return listSegment;
}

// 默认导出组件
export default VirtualWaterfall;
